import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js"
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { GatedConfirmSetupIntentWalletFailReason } from "ipctg-inflow-types/InFlowEnums"
import { FormEvent, useState } from "react"
import { useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import StripeElementsWrapper from "@/globalComponents/molecules/StripeElementsWrapper"
import {
  ExpectedError,
  GatedConfirmSetupIntentWalletError,
} from "@/utilities/expectedErrors"
import {
  savedPaymentMethodsOptions,
  walletSetupIntentOptions,
} from "@/utilities/hooks/queries/user"
import { useStripePaymentElement } from "@/utilities/hooks/useStripePaymentElement"
import { log } from "@/utilities/logging"
import { Crumb } from "@/utilities/types/local"

export const Route = createFileRoute(
  "/_authenticated/account/saved-payment-methods/add"
)({
  component: RouteComponent,
  beforeLoad: ({ context }) => {
    context.queryClient.ensureQueryData(walletSetupIntentOptions())
  },
  loader: () => {
    return {
      crumb: {
        translationKey: "common:account.savedPaymentMethods.header",
      } as Crumb,
    }
  },
})

function RouteComponent() {
  const { t } = useTranslation(["common", "errors"])
  const navigate = Route.useNavigate()
  const search = Route.useSearch()
  const { data: setupIntentWalletRsp } = useSuspenseQuery(
    walletSetupIntentOptions()
  )

  return (
    <Card>
      <Heading level={2}>
        {t("account.savedPaymentMethods.add.header")}
      </Heading>
      <StripeElementsWrapper
        clientSecret={setupIntentWalletRsp.clientSecret}
        flow="misc"
      >
        <CardForm
          goNext={() =>
            navigate({
              to: "/account/saved-payment-methods",
              search,
            })
          }
        />
      </StripeElementsWrapper>
    </Card>
  )
}

interface CardFormProps {
  goNext: () => void
}

function CardForm(props: CardFormProps) {
  const stripe = useStripe()
  const elements = useElements()
  const queryClient = useQueryClient()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error>()
  const { t } = useTranslation()

  const { paymentElementProps, stripeError } = useStripePaymentElement({
    flow: "misc",
  })

  if (stripeError) throw stripeError

  return (
    <form onSubmit={handleSubmit}>
      <fieldset disabled={!stripe || !elements || loading}>
        <PaymentElement
          {...paymentElementProps}
          options={{
            layout: "tabs",
            paymentMethodOrder: ["card"],
            fields: {
              billingDetails: {
                address: {
                  country: "auto",
                },
              },
            },
            terms: {
              card: "never",
            },
          }}
        />

        {error && (
          <FieldError>
            {error instanceof ExpectedError
              ? t(error.translationKey, { ns: "errors" })
              : error.message}
          </FieldError>
        )}
        <Button type="submit" buttonType="primary" isPending={loading}>
          {t("common.save")}
        </Button>
      </fieldset>
    </form>
  )

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setLoading(true)

    if (!stripe || !elements) {
      setLoading(false)
      return
    }

    const paymentElement = elements.getElement(PaymentElement)
    if (!paymentElement) {
      setError(new Error(t("common.paymentDetailsNotFound", { ns: "errors" })))
      setLoading(false)
      return
    }

    try {
      const result = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
      })

      if (result.error) {
        log({
          level: "error",
          flow: "misc",
          scope: "payment.stripeConfirmSetup",
          message: "Stripe confirmSetup returned an error",
          meta: {
            code: result.error.code,
            type: result.error.type,
            declineCode: result.error.decline_code,
          },
          sentry: "exception",
          exception: result.error,
        })
        setError(new Error(result.error.message))
        return
      }

      const { cognitoIDToken } = await api.auth.fetchAuthSession()
      if (!cognitoIDToken) {
        throw new GatedConfirmSetupIntentWalletError(
          GatedConfirmSetupIntentWalletFailReason.Unauthorized
        )
      }
      const rsp = await api.confirmSetupIntentWallet.POST({
        cognitoIDToken,
        setupIntentID: result.setupIntent.id,
      })
      if ("failReason" in rsp) {
        throw new GatedConfirmSetupIntentWalletError(rsp.failReason)
      }

      queryClient.setQueryData(savedPaymentMethodsOptions().queryKey, {
        paymentMethods: rsp.paymentMethods,
      })
      props.goNext()
    } catch (e: unknown) {
      log({
        level: "error",
        flow: "misc",
        scope: "payment.confirmSetup",
        message: "Saved payment method setup failed",
        meta: { error: e },
        sentry: e instanceof ExpectedError ? "breadcrumb" : "exception",
        exception: e instanceof Error ? e : new Error(String(e)),
      })
      if (e instanceof Error) {
        setError(e)
      } else {
        setError(new Error(t("common.somethingWentWrong", { ns: "errors" })))
      }
    } finally {
      setLoading(false)
    }
  }
}
